import React, { useState, useEffect, useRef, useCallback } from 'react';
import { WS_URL, WS_CLOSE_REVOKED, WS_CLOSE_TOKEN_EXPIRED } from '../config/env';
import { getAccessToken, notifySessionLost, onSessionRestored } from '../auth/session';
import { messageBoardService } from '../services/messageBoardService';
import ClippySVG from '../components/ClippySVG';

// =====================================================
// GVC DISPLAY - OPTIMIZED FOR 85" 4K TV (QN85Q7FAAFXZA)
// =====================================================

const BRONX_COORDS = { lat: 40.8448, lon: -73.8648 };

const parseUTCTimestamp = (timestamp) => {
  if (!timestamp) return new Date();
  const ts = String(timestamp);
  if (!ts.includes('Z') && !ts.includes('+') && !ts.includes('-', 10)) {
    return new Date(ts + 'Z');
  }
  return new Date(ts);
};

const parseDateLocal = (dateStr) => {
  if (!dateStr) return new Date();
  const dateOnly = String(dateStr).split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getWeatherIcon = (code, isDay = true) => {
  if (code === 0) return isDay ? '☀️' : '🌙';
  if (code === 1 || code === 2) return isDay ? '⛅' : '☁️';
  if (code === 3) return '☁️';
  if (code >= 45 && code <= 48) return '🌫️';
  if (code >= 51 && code <= 67) return '🌧️';
  if (code >= 71 && code <= 77) return '❄️';
  if (code >= 80 && code <= 82) return '🌧️';
  if (code >= 85 && code <= 86) return '❄️';
  if (code >= 95) return '⛈️';
  return '☁️';
};

const getWeatherDescription = (code) => {
  if (code === 0) return 'Clear';
  if (code === 1) return 'Mostly Clear';
  if (code === 2) return 'Partly Cloudy';
  if (code === 3) return 'Overcast';
  if (code >= 45 && code <= 48) return 'Foggy';
  if (code >= 51 && code <= 55) return 'Drizzle';
  if (code >= 61 && code <= 65) return 'Rain';
  if (code >= 71 && code <= 75) return 'Snow';
  if (code >= 80 && code <= 82) return 'Showers';
  if (code >= 95) return 'Thunderstorm';
  return 'Unknown';
};

// Audio System
const createAudioContext = () => {
  try { return new (window.AudioContext || window.webkitAudioContext)(); }
  catch (e) { return null; }
};

const playNotificationTone = (audioContext, isUrgent = false) => {
  if (!audioContext) return;
  try {
    if (audioContext.state === 'suspended') audioContext.resume();
    const now = audioContext.currentTime;
    if (isUrgent) {
      [880, 1100, 880].forEach((freq, i) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain); gain.connect(audioContext.destination);
        osc.frequency.value = freq; osc.type = 'square';
        gain.gain.setValueAtTime(0.5, now + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.01, now + (i + 1) * 0.15);
        osc.start(now + i * 0.15); osc.stop(now + (i + 1) * 0.15);
      });
    } else {
      [523, 659].forEach((freq, i) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain); gain.connect(audioContext.destination);
        osc.frequency.value = freq; osc.type = 'sine';
        gain.gain.setValueAtTime(0.4, now + i * 0.25);
        gain.gain.exponentialRampToValueAtTime(0.01, now + (i + 1) * 0.25 + 0.1);
        osc.start(now + i * 0.25); osc.stop(now + (i + 1) * 0.25 + 0.15);
      });
    }
  } catch (e) {}
};

const playReminderTone = (audioContext) => {
  if (!audioContext) return;
  try {
    if (audioContext.state === 'suspended') audioContext.resume();
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    osc.connect(gain); gain.connect(audioContext.destination);
    osc.frequency.value = 440; osc.type = 'sine';
    const now = audioContext.currentTime;
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
    osc.start(now); osc.stop(now + 0.3);
  } catch (e) {}
};

// WebSocket URL comes from config/env.js (P1-B)
const getWsUrl = () => WS_URL;

const resampleAudio = (inputData, inputSampleRate, outputSampleRate) => {
  if (inputSampleRate === outputSampleRate) return inputData;
  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.floor(inputData.length / ratio);
  const output = new Float32Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio;
    const srcIndexFloor = Math.floor(srcIndex);
    const srcIndexCeil = Math.min(srcIndexFloor + 1, inputData.length - 1);
    const fraction = srcIndex - srcIndexFloor;
    output[i] = inputData[srcIndexFloor] * (1 - fraction) + inputData[srcIndexCeil] * fraction;
  }
  return output;
};

const Display = () => {
  const [displayData, setDisplayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [flashState, setFlashState] = useState(false);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  
  const audioContextRef = useRef(null);
  const previousPageIdsRef = useRef(new Set());
  const reminderIntervalRef = useRef(null);
  const audioInitializedRef = useRef(false);
  
  const [voiceActive, setVoiceActive] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const voiceAudioContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0);

  const initializeAudio = useCallback(() => {
    if (!audioInitializedRef.current) {
      audioContextRef.current = createAudioContext();
      audioInitializedRef.current = true;
    }
  }, []);

  const initializeVoiceAudio = useCallback(() => {
    if (!voiceAudioContextRef.current) {
      voiceAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return voiceAudioContextRef.current;
  }, []);

  const playAudioQueue = useCallback(async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) return;
    isPlayingRef.current = true;
    const audioContext = initializeVoiceAudio();
    if (audioContext.state === 'suspended') await audioContext.resume();
    const outputSampleRate = audioContext.sampleRate;
    
    while (audioQueueRef.current.length > 0) {
      const audioData = audioQueueRef.current.shift();
      try {
        const int16Array = new Int16Array(audioData);
        const float32Array = new Float32Array(int16Array.length);
        for (let i = 0; i < int16Array.length; i++) float32Array[i] = int16Array[i] / 32768;
        const resampledData = resampleAudio(float32Array, 16000, outputSampleRate);
        const audioBuffer = audioContext.createBuffer(1, resampledData.length, outputSampleRate);
        audioBuffer.getChannelData(0).set(resampledData);
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 3.0;
        source.connect(gainNode);
        gainNode.connect(audioContext.destination);
        const now = audioContext.currentTime;
        const startTime = Math.max(now, nextPlayTimeRef.current);
        source.start(startTime);
        nextPlayTimeRef.current = startTime + audioBuffer.duration;
      } catch (e) {}
    }
    isPlayingRef.current = false;
  }, [initializeVoiceAudio]);

  // P1-B: a fresh access token on every (re)connect (the session module
  // refreshes silently), exponential backoff 1s→30s, and the server's close
  // codes honoured: 4408 = token expired → quick reconnect with a fresh
  // token; 4401 = session revoked → stop and show the sign-in modal.
  const reconnectTimerRef = useRef(null);
  const attemptRef = useRef(0);
  const stoppedRef = useRef(false);
  const connectRef = useRef(null);

  const scheduleReconnect = useCallback((quick = false) => {
    if (stoppedRef.current) return;
    const delay = quick ? 1000 : Math.min(30000, 1000 * 2 ** attemptRef.current);
    attemptRef.current += 1;
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => { if (connectRef.current) connectRef.current(); }, delay);
  }, []);

  const connectVoiceWebSocket = useCallback(async () => {
    if (stoppedRef.current) return;
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;
    const token = await getAccessToken();
    if (!token) { scheduleReconnect(); return; }
    const ws = new WebSocket(getWsUrl(), ['gvc-portal', token]);
    ws.binaryType = 'arraybuffer';
    
    ws.onopen = () => {
      attemptRef.current = 0;
      setWsConnected(true);
      ws.send(JSON.stringify({ type: 'register', role: 'listener' }));
    };
    
    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'broadcast_start') { setVoiceActive(true); initializeVoiceAudio(); nextPlayTimeRef.current = 0; }
          else if (message.type === 'broadcast_stop') { setVoiceActive(false); audioQueueRef.current = []; }
          else if (message.type === 'status' && message.broadcasting) { setVoiceActive(true); }
        } catch (e) {}
      } else {
        audioQueueRef.current.push(event.data);
        setVoiceActive(true);
        playAudioQueue();
      }
    };
    
    ws.onerror = () => setWsConnected(false);
    ws.onclose = (event) => {
      setWsConnected(false);
      setVoiceActive(false);
      if (event.code === WS_CLOSE_REVOKED) {
        stoppedRef.current = true;
        notifySessionLost({ code: 'SESSION_REVOKED' });
        return;
      }
      scheduleReconnect(event.code === WS_CLOSE_TOKEN_EXPIRED);
    };
    wsRef.current = ws;
  }, [initializeVoiceAudio, playAudioQueue, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connectVoiceWebSocket;
    stoppedRef.current = false;
    connectVoiceWebSocket();
    // After the sign-in modal succeeds, reconnect with the new session.
    const offRestored = onSessionRestored(() => { stoppedRef.current = false; attemptRef.current = 0; connectVoiceWebSocket(); });
    return () => {
      stoppedRef.current = true;
      offRestored();
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
      if (voiceAudioContextRef.current) voiceAudioContextRef.current.close();
    };
  }, [connectVoiceWebSocket]);

  useEffect(() => {
    initializeAudio();
    const handleClick = () => {
      initializeAudio();
      initializeVoiceAudio();
      if (audioContextRef.current?.state === 'suspended') audioContextRef.current.resume();
      if (voiceAudioContextRef.current?.state === 'suspended') voiceAudioContextRef.current.resume();
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [initializeAudio, initializeVoiceAudio]);

  useEffect(() => {
    fetchDisplayData();
    fetchWeather();
    const interval = setInterval(fetchDisplayData, 10000);
    const weatherInterval = setInterval(fetchWeather, 5 * 60 * 1000);
    return () => { clearInterval(interval); clearInterval(weatherInterval); };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const activePages = getVisiblePages();
    if (activePages.length > 0) {
      const flashTimer = setInterval(() => setFlashState(prev => !prev), 500);
      return () => clearInterval(flashTimer);
    }
  }, [displayData]);

  useEffect(() => {
    if (!displayData?.pages) return;
    const currentPages = getVisiblePages();
    const currentPageIds = new Set(currentPages.map(p => p.id));
    const newPages = currentPages.filter(p => !previousPageIdsRef.current.has(p.id));
    if (newPages.length > 0) {
      const hasUrgentNew = newPages.some(p => p.priority === 'urgent' || p.priority === 'high');
      playNotificationTone(audioContextRef.current, hasUrgentNew);
    }
    previousPageIdsRef.current = currentPageIds;
  }, [displayData]);

  useEffect(() => {
    const activePages = getVisiblePages();
    if (activePages.length > 0) {
      if (reminderIntervalRef.current) clearInterval(reminderIntervalRef.current);
      reminderIntervalRef.current = setInterval(() => {
        const hasUrgent = activePages.some(p => p.priority === 'urgent' || p.priority === 'high');
        if (hasUrgent) playNotificationTone(audioContextRef.current, true);
        else playReminderTone(audioContextRef.current);
      }, 30000);
      return () => { if (reminderIntervalRef.current) clearInterval(reminderIntervalRef.current); };
    } else {
      if (reminderIntervalRef.current) { clearInterval(reminderIntervalRef.current); reminderIntervalRef.current = null; }
    }
  }, [displayData]);

  const fetchDisplayData = async () => {
    try {
      const data = await messageBoardService.getDisplay();
      setDisplayData(data);
    } catch (err) {
      setDisplayData({ messages: [], events: [], pages: [], settings: {} });
    } finally { setLoading(false); }
  };

  const fetchWeather = async () => {
    try {
      setWeatherLoading(true);
      const response = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${BRONX_COORDS.lat}&longitude=${BRONX_COORDS.lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,is_day&daily=temperature_2m_max,temperature_2m_min,weather_code&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FNew_York`);
      const data = await response.json();
      setWeather(data);
    } catch (err) { setWeather(null); }
    finally { setWeatherLoading(false); }
  };

  const getVisiblePages = () => {
    let pages = displayData?.pages?.filter(p => p.is_active && !p.is_acknowledged) || [];
    pages = pages.filter(p => p.audience === 'internal' || p.audience === 'all' || !p.audience);
    pages.sort((a, b) => {
      const aUrgent = a.priority === 'urgent' || a.priority === 'high';
      const bUrgent = b.priority === 'urgent' || b.priority === 'high';
      if (aUrgent && !bUrgent) return -1;
      if (!aUrgent && bUrgent) return 1;
      return parseUTCTimestamp(a.created_at) - parseUTCTimestamp(b.created_at);
    });
    return pages;
  };

  const formatTime = (date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const formatDate = (date) => date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading) {
    return (
      <div style={tvStyles.loadingScreen}>
        <ClippySVG size={500} />
        <h1 style={tvStyles.loadingText}>Loading Display...</h1>
      </div>
    );
  }

  const settings = displayData?.settings || {};
  const visiblePages = getVisiblePages();
  const hasActivePages = visiblePages.length > 0;
  const hasUrgent = visiblePages.some(p => p.priority === 'urgent' || p.priority === 'high');

  return (
    <div style={{ ...tvStyles.container, backgroundColor: hasActivePages ? (hasUrgent ? (flashState ? '#cc0000' : '#0f172a') : (flashState ? '#D97706' : '#0f172a')) : '#0f172a' }}>
      {/* Voice Broadcast Overlay */}
      {voiceActive && (
        <div style={tvStyles.voiceOverlay}>
          <div style={tvStyles.voiceContent}>
            <span style={{ fontSize: 200 }}>🎤</span>
            <div style={tvStyles.voiceText}>VOICE BROADCAST</div>
            <div style={tvStyles.voiceSubtext}>Listen for announcement</div>
          </div>
        </div>
      )}
      
      {hasActivePages ? (
        <TVPageAlert pages={visiblePages} settings={settings} currentTime={currentTime} flashState={flashState} formatTime={formatTime} formatDate={formatDate} hasUrgent={hasUrgent} />
      ) : (
        <TVDashboard displayData={displayData} weather={weather} weatherLoading={weatherLoading} settings={settings} currentTime={currentTime} formatTime={formatTime} formatDate={formatDate} wsConnected={wsConnected} />
      )}
    </div>
  );
};

// =====================================================
// TV DASHBOARD - Large fonts for 85" 4K display
// =====================================================
const TVDashboard = ({ displayData, weather, weatherLoading, currentTime, formatTime, formatDate, settings, wsConnected }) => {
  const currentWeather = weather?.current;
  const dailyWeather = weather?.daily;
  const isDay = currentWeather?.is_day === 1;

  return (
    <div style={tvStyles.dashboard}>
      {/* Header */}
      <div style={tvStyles.header}>
        <div style={tvStyles.logoSection}>
          <img src="/precious-cargo-logo.png" alt="GVC" style={tvStyles.logo} onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <div style={tvStyles.companyName}>{settings.company_name || 'GVC School Bus'}</div>
            <div style={tvStyles.tagline}>Precious Cargo Since 1991</div>
          </div>
        </div>
        <div style={tvStyles.timeSection}>
          <div style={tvStyles.time}>{formatTime(currentTime)}</div>
          <div style={tvStyles.date}>{formatDate(currentTime)}</div>
        </div>
      </div>

      {/* Main Content - 3 Column Grid */}
      <div style={tvStyles.mainGrid}>
        {/* Weather Column */}
        <div style={tvStyles.weatherCard}>
          {weatherLoading ? (
            <div style={tvStyles.centerContent}>
              <span style={{ fontSize: 150 }}>🌤️</span>
              <span style={{ color: '#94a3b8', fontSize: 48 }}>Loading...</span>
            </div>
          ) : currentWeather ? (
            <>
              <div style={tvStyles.weatherMain}>
                <span style={tvStyles.weatherIcon}>{getWeatherIcon(currentWeather.weather_code, isDay)}</span>
                <div style={tvStyles.tempSection}>
                  <div style={tvStyles.temperature}>{Math.round(currentWeather.temperature_2m)}°</div>
                  <div style={tvStyles.weatherDesc}>{getWeatherDescription(currentWeather.weather_code)}</div>
                </div>
              </div>
              <div style={tvStyles.weatherDetails}>
                <div style={tvStyles.weatherDetail}>
                  <span style={{ fontSize: 60 }}>💨</span>
                  <span style={{ fontSize: 48 }}>{Math.round(currentWeather.wind_speed_10m)} mph</span>
                </div>
                <div style={tvStyles.weatherDetail}>
                  <span style={{ fontSize: 60 }}>💧</span>
                  <span style={{ fontSize: 48 }}>{currentWeather.relative_humidity_2m}%</span>
                </div>
                {dailyWeather && (
                  <div style={tvStyles.weatherDetail}>
                    <span style={{ fontSize: 60 }}>📊</span>
                    <span style={{ fontSize: 42 }}>H:{Math.round(dailyWeather.temperature_2m_max[0])}° L:{Math.round(dailyWeather.temperature_2m_min[0])}°</span>
                  </div>
                )}
              </div>
              <div style={tvStyles.location}>📍 Bronx, NY</div>
            </>
          ) : (
            <div style={tvStyles.centerContent}>
              <span style={{ fontSize: 100 }}>⚠️</span>
              <span style={{ fontSize: 42, color: '#94a3b8' }}>Weather unavailable</span>
            </div>
          )}
        </div>

        {/* Announcements Column */}
        <div style={tvStyles.contentCard}>
          <div style={tvStyles.cardHeader}>
            <span style={{ fontSize: 80 }}>📢</span>
            <span style={tvStyles.cardTitle}>Announcements</span>
          </div>
          <div style={tvStyles.cardBody}>
            {displayData?.messages?.length > 0 ? (
              displayData.messages.slice(0, 2).map((msg) => (
                <div key={msg.id} style={tvStyles.listItem}>
                  <div style={{...tvStyles.dot, backgroundColor: msg.priority === 'urgent' ? '#ef4444' : msg.priority === 'high' ? '#f59e0b' : '#22c55e'}} />
                  <div style={tvStyles.itemContent}>
                    <div style={tvStyles.itemTitle}>{msg.title}</div>
                    <div style={tvStyles.itemMeta}>{msg.category || 'General'}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={tvStyles.emptyState}>
                <span style={{ fontSize: 120 }}>✨</span>
                <span style={{ fontSize: 48 }}>No announcements</span>
              </div>
            )}
          </div>
        </div>

        {/* Events Column */}
        <div style={tvStyles.contentCard}>
          <div style={tvStyles.cardHeader}>
            <span style={{ fontSize: 80 }}>📅</span>
            <span style={tvStyles.cardTitle}>Upcoming Events</span>
          </div>
          <div style={tvStyles.cardBody}>
            {displayData?.events?.length > 0 ? (
              displayData.events.slice(0, 2).map((event) => {
                const eventDate = parseDateLocal(event.event_date);
                return (
                  <div key={event.id} style={tvStyles.eventItem}>
                    <div style={tvStyles.eventDateBox}>
                      <div style={tvStyles.eventMonth}>{eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</div>
                      <div style={tvStyles.eventDay}>{eventDate.getDate()}</div>
                    </div>
                    <div style={tvStyles.itemContent}>
                      <div style={tvStyles.itemTitle}>{event.title}</div>
                      {event.event_time && <div style={tvStyles.itemMeta}>🕐 {event.event_time}</div>}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={tvStyles.emptyState}>
                <span style={{ fontSize: 120 }}>📆</span>
                <span style={{ fontSize: 48 }}>No upcoming events</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={tvStyles.footer}>
        <div style={tvStyles.footerLeft}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={tvStyles.statusDot} />
              <span style={{ fontSize: 36, color: '#94a3b8' }}>System Online</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginLeft: 32 }}>
              <span style={{ fontSize: 24, color: '#64748b' }}>🔊 Audio Ready</span>
              {wsConnected
                ? <span style={{ fontSize: 24, color: '#22c55e' }}>● Connected</span>
                : <span style={{ fontSize: 20, color: '#64748b', opacity: 0.75 }} title="Voice paging is reconnecting">○ Voice paging offline</span>}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 32, color: '#475569' }}>Powered by SVT Consulting</div>
        <ClippySVG size={150} />
      </div>
    </div>
  );
};

// =====================================================
// TV PAGE ALERT - Shows ALL pages in readable grid
// =====================================================
const TVPageAlert = ({ pages, settings, currentTime, flashState, formatTime, formatDate, hasUrgent }) => {
  // Determine grid layout based on number of pages
  const getGridColumns = (count) => {
    if (count === 1) return '1fr';
    if (count === 2) return '1fr 1fr';
    if (count <= 4) return '1fr 1fr';
    return '1fr 1fr 1fr';
  };
  
  const getGridRows = (count) => {
    if (count <= 2) return '1fr';
    if (count <= 4) return '1fr 1fr';
    return '1fr 1fr';
  };

  return (
    <div style={tvStyles.alertContainer}>
      {/* Flashing Border */}
      <div style={{ ...tvStyles.flashBorder, borderColor: flashState ? '#F59E0B' : 'transparent' }} />
      
      {/* Alert Header */}
      <div style={{ ...tvStyles.alertHeader, background: hasUrgent ? 'linear-gradient(90deg, #dc2626, #b91c1c)' : 'linear-gradient(90deg, #d97706, #b45309)' }}>
        <div style={tvStyles.alertTitle}>
          <span style={{ fontSize: 100 }}>{hasUrgent ? '🚨' : '📟'}</span>
          <div>
            <div style={{ fontSize: 72, fontWeight: 'bold', letterSpacing: 6 }}>ATTENTION REQUIRED</div>
            <div style={{ fontSize: 36 }}>{pages.length} active page{pages.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div style={tvStyles.alertClock}>
          <div style={{ fontSize: 84, fontWeight: 'bold', fontFamily: 'monospace' }}>{formatTime(currentTime)}</div>
          <div style={{ fontSize: 36 }}>{formatDate(currentTime)}</div>
        </div>
      </div>

      {/* All Pages Grid */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: getGridColumns(pages.length),
        gridTemplateRows: getGridRows(pages.length),
        gap: 30,
        padding: '30px 50px',
        minHeight: 0,
        overflow: 'hidden'
      }}>
        {pages.slice(0, 6).map((page, index) => {
          const isPageUrgent = page.priority === 'urgent' || page.priority === 'high';
          return (
            <div key={page.id} style={{
              background: 'white',
              borderRadius: 40,
              padding: pages.length === 1 ? '60px 80px' : '40px 50px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              border: isPageUrgent ? '8px solid #dc2626' : '8px solid #f59e0b',
              transform: isPageUrgent && flashState ? 'scale(1.02)' : 'scale(1)',
              transition: 'transform 0.3s'
            }}>
              <div style={{ fontSize: pages.length === 1 ? 48 : 36, color: '#666', marginBottom: 10 }}>
                📢 PAGING:
              </div>
              <div style={{ 
                fontSize: pages.length === 1 ? 100 : (pages.length <= 2 ? 80 : 60), 
                fontWeight: 'bold', 
                color: isPageUrgent ? '#dc2626' : '#d97706', 
                margin: '20px 0',
                lineHeight: 1.1
              }}>
                {page.employee_name}
              </div>
              <div style={{ 
                fontSize: pages.length === 1 ? 56 : (pages.length <= 2 ? 44 : 36), 
                color: '#333', 
                lineHeight: 1.4, 
                marginBottom: 30,
                maxHeight: pages.length > 2 ? '120px' : 'auto',
                overflow: 'hidden'
              }}>
                {page.message}
              </div>
              <div style={{
                display: 'inline-block',
                padding: pages.length === 1 ? '24px 60px' : '16px 40px',
                borderRadius: 50,
                fontSize: pages.length === 1 ? 40 : 32,
                fontWeight: 'bold',
                letterSpacing: 3,
                color: 'white',
                backgroundColor: page.priority === 'urgent' ? '#dc2626' : page.priority === 'high' ? '#f59e0b' : '#3b82f6',
                transform: isPageUrgent && flashState ? 'scale(1.1)' : 'scale(1)',
                transition: 'transform 0.2s'
              }}>
                {page.priority?.toUpperCase()} PRIORITY
              </div>
            </div>
          );
        })}
      </div>

      {/* Show overflow indicator if more than 6 pages */}
      {pages.length > 6 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          fontSize: 36, 
          color: '#f59e0b',
          background: 'rgba(0,0,0,0.5)'
        }}>
          +{pages.length - 6} more pages waiting...
        </div>
      )}

      {/* Footer */}
      <div style={tvStyles.alertFooter}>
        <div style={{ fontSize: 36, color: '#999' }}>{settings.company_name || 'GVC School Bus'}</div>
        <div style={{ fontSize: 48, fontWeight: 'bold', color: '#f59e0b', opacity: flashState ? 1 : 0.3 }}>
          ⚡ PLEASE RESPOND IMMEDIATELY ⚡
        </div>
        <div style={{ fontSize: 36, color: '#999' }}>🏢 Internal Alert</div>
      </div>
    </div>
  );
};

// =====================================================
// TV STYLES - Optimized for 85" 4K (3840x2160)
// All sizes are 2.5-3x larger than standard display
// =====================================================
const tvStyles = {
  container: {
    minHeight: '100vh',
    width: '100vw',
    color: 'white',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflow: 'hidden',
    position: 'relative',
    transition: 'background-color 0.3s'
  },
  
  // Voice overlay
  voiceOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(220, 38, 38, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999
  },
  voiceContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 40
  },
  voiceText: { fontSize: 120, fontWeight: 'bold', letterSpacing: 8 },
  voiceSubtext: { fontSize: 56, opacity: 0.9 },
  
  // Loading
  loadingScreen: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 60,
    backgroundColor: '#0f172a'
  },
  loadingText: { color: '#f59e0b', fontSize: 96 },
  
  // Dashboard
  dashboard: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
  },
  
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '40px 60px',
    background: 'linear-gradient(90deg, rgba(245,158,11,0.15) 0%, rgba(245,158,11,0.05) 100%)',
    borderBottom: '4px solid rgba(245,158,11,0.3)'
  },
  logoSection: { display: 'flex', alignItems: 'center', gap: 40 },
  logo: { height: 180, width: 'auto' },
  companyName: { fontSize: 72, fontWeight: 700, color: '#f59e0b' },
  tagline: { fontSize: 36, color: '#94a3b8', letterSpacing: 3, marginTop: 8 },
  timeSection: { textAlign: 'right' },
  time: { fontSize: 120, fontWeight: 700, letterSpacing: -2 },
  date: { fontSize: 48, color: '#94a3b8', marginTop: 8 },
  
  // Main grid
  mainGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1.2fr 1.2fr',
    gap: 40,
    padding: 40,
    minHeight: 0
  },
  
  // Weather card
  weatherCard: {
    background: 'linear-gradient(135deg, rgba(59,130,246,0.3), rgba(37,99,235,0.2))',
    borderRadius: 32,
    padding: 50,
    border: '4px solid rgba(59,130,246,0.3)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  weatherMain: { display: 'flex', alignItems: 'center', gap: 30 },
  weatherIcon: { fontSize: 180 },
  tempSection: { flex: 1 },
  temperature: { fontSize: 160, fontWeight: 700, lineHeight: 1 },
  weatherDesc: { fontSize: 48, color: '#93c5fd', marginTop: 10 },
  weatherDetails: { display: 'flex', flexDirection: 'column', gap: 30, marginTop: 40 },
  weatherDetail: { display: 'flex', alignItems: 'center', gap: 20 },
  location: { fontSize: 36, color: '#64748b', marginTop: 30 },
  centerContent: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 },
  
  // Content cards
  contentCard: {
    background: 'rgba(30,41,59,0.9)',
    borderRadius: 32,
    border: '4px solid rgba(148,163,184,0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '36px 50px',
    background: 'rgba(245,158,11,0.15)',
    borderBottom: '4px solid rgba(245,158,11,0.25)'
  },
  cardTitle: { fontSize: 56, fontWeight: 600, color: '#f59e0b' },
  cardBody: {
    flex: 1,
    padding: 40,
    display: 'flex',
    flexDirection: 'column',
    gap: 30,
    overflow: 'hidden'
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 30,
    padding: 40,
    background: 'rgba(15,23,42,0.7)',
    borderRadius: 24,
    border: '2px solid rgba(148,163,184,0.15)'
  },
  dot: { width: 28, height: 28, borderRadius: '50%', marginTop: 16, flexShrink: 0 },
  itemContent: { flex: 1, minWidth: 0 },
  itemTitle: { fontSize: 48, fontWeight: 500, lineHeight: 1.4 },
  itemMeta: { fontSize: 36, color: '#64748b', marginTop: 12 },
  eventItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 30,
    padding: 36,
    background: 'rgba(15,23,42,0.7)',
    borderRadius: 24,
    border: '2px solid rgba(148,163,184,0.15)'
  },
  eventDateBox: {
    width: 140,
    height: 140,
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    borderRadius: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  eventMonth: { fontSize: 28, fontWeight: 700, color: 'rgba(0,0,0,0.6)' },
  eventDay: { fontSize: 64, fontWeight: 700, color: '#0f172a', lineHeight: 1 },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    gap: 20
  },
  
  // Footer
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '30px 60px',
    background: 'rgba(0,0,0,0.5)',
    borderTop: '4px solid rgba(148,163,184,0.2)'
  },
  footerLeft: { display: 'flex', alignItems: 'center', gap: 20 },
  statusDot: { width: 20, height: 20, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 20px rgba(34,197,94,0.6)' },
  
  // Alert styles
  alertContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  flashBorder: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    border: '24px solid transparent',
    pointerEvents: 'none',
    transition: 'all 0.2s',
    zIndex: 10
  },
  alertHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '40px 60px',
    zIndex: 20
  },
  alertTitle: { display: 'flex', alignItems: 'center', gap: 30 },
  alertClock: { textAlign: 'right' },
  
  alertFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '30px 60px',
    background: 'rgba(0,0,0,0.95)',
    zIndex: 20
  }
};

export default Display;
